"use client";

import React, { useMemo, useRef, useState } from "react";
import OperatorsTableActionBar from "./OperatorsTableActionBar";
import {
  Box,
  Button,
  Card,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import OperatorsTableInfoModal from "./OperatorsTableInfoModal";
import CustomChip from "../CustomChip/CustomChip";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import useFetchWithAuth from "../../features/hooks/useFetchWithAuth";
import StyledTableCell from "../ui/TableCell/TableCell";

const headers = [
  "Оператор",
  "Прийняті",
  "З допрод.",
  "Без допрод.",
  "Кросів",
  "Маржа доп.",
  "% доп.",
  "Сер. чек",
  "Лідів/год",
  "ЗП",
  "",
];

const options = [
  { value: "accepted_count", label: "Прийняті" },
  { value: "orders_with_upsell", label: "З допрод." },
  { value: "orders_without_upsell", label: "Без допрод." },
  { value: "cross_count", label: "Кросів" },
  { value: "upsell_margin_sum", label: "Маржа доп." },
  { value: "upsell_percent", label: "% доп." },
  { value: "avg_check", label: "Сер. чек" },
  { value: "leads_per_hour", label: "Лідів/год" },
  { value: "operator_salary", label: "ЗП" },
];

function getKpiColor(type, value) {
  switch (type) {
    case "upsell_percent":
      if (value >= 72) return "lightgreen";
      if (value >= 60) return "khaki";
      return "lightcoral";

    case "avg_check":
      if (value >= 140) return "lightgreen";
      if (value >= 100) return "khaki";
      if (value < 90) return "lightcoral";
      return "khaki";

    case "leads_per_hour":
      if (value >= 5.8) return "lightgreen";
      if (value >= 4) return "khaki";
      return "lightcoral";

    default:
      return "transparent";
  }
}

export default function OperatorsTableContainer({
  onNavigate,
  selectedDate,
  onAdd,
}) {
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState("desc");
  const [modalType, setModalType] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [activeDate, setActiveDate] = useState(() => dayjs().format("YYYY-MM-DD"));

  const modalRef = useRef(null);
  const dateForRequest = useMemo(() => {
    const externalDate = selectedDate ? dayjs(selectedDate) : null;
    if (externalDate && externalDate.isValid()) {
      return externalDate.format("YYYY-MM-DD");
    }
    return activeDate;
  }, [selectedDate, activeDate]);
  const url = `/statistic/operator/ratings/?date_from=${dateForRequest}&date_to=${dateForRequest}`;

  const { data: apiResponse, loading, error } = useFetchWithAuth(url);

  const operators = Array.isArray(apiResponse?.data) ? apiResponse.data : [];
  const normalizedOperators = useMemo(() => {
    return operators.map((op) => ({
      ...op,
      full_name:
        (op.full_name ??
          `${op.first_name || ""} ${op.last_name || ""}`.trim()) ||
        op.username ||
        "-",
      accepted_count: op.accepted_count ?? 0,
      orders_with_upsell: op.orders_with_upsell ?? 0,
      orders_without_upsell: op.orders_without_upsell ?? 0,
      cross_count: op.cross_count ?? op.cross ?? 0,
      upsell_margin_sum: op.upsell_margin_sum ?? op.totla_upsell ?? 0,
      upsell_percent: op.upsell_percent ?? op.percent_upsell ?? 0,
      avg_check: op.avg_check ?? op.average_check ?? 0,
      leads_per_hour:
        op.leads_per_hour ?? op.orders_per_hour ?? op.leadsPerHour ?? 0,
      operator_salary:
        op.operator_salary ?? op.salary_amount ?? op.salary ?? 0,
    }));
  }, [operators]);
  const handleRowClick = (op) => {
    if (!onNavigate) return;
    const operatorId = op.operator_id ?? op.id;
    if (!operatorId) return;
    onNavigate(operatorId);
  };

  const handleSortOrderClick = () => {
    setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
  };
  const handleSortChange = (field) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
  };

  const handleCalendarClick = () => {
    setModalType("calendar");
    setOpenModal(true);
  };

  const handleDateSelect = (date) => {
    const parsed = dayjs(date);
    if (!parsed.isValid()) return;
    setActiveDate(parsed.format("YYYY-MM-DD"));
  };

  const sortedOperators = [...normalizedOperators].sort((a, b) => {
    if (!sortBy) return 0;

    const valA = a[sortBy] ?? 0;
    const valB = b[sortBy] ?? 0;

    if (typeof valA === "string") {
      return sortOrder === "asc"
        ? valA.localeCompare(valB)
        : valB.localeCompare(valA);
    } else {
      return sortOrder === "asc" ? valA - valB : valB - valA;
    }
  });

  const filteredOperators = sortedOperators.filter((op) =>
    op.full_name?.toLowerCase().includes(search.toLowerCase())
  );

  const handleResetFilters = () => {
    setSearch("");
    setSortBy("");
    setSortOrder("desc");
  };

  return (
    <>
      <Card sx={{ p: 2 }}>
        <OperatorsTableActionBar
          title="Список операторів"
          options={options}
          sortBy={sortBy}
          onSortChange={handleSortChange}
          sortOrderLabel={sortOrder === "asc" ? "Зростання" : "Спадання"}
          onSortOrderClick={handleSortOrderClick}
          searchValue={search}
          onSearchChange={(e) => setSearch(e.target.value)}
          onCalendarClick={handleCalendarClick}
          onResetFilters={handleResetFilters}
          onAdd={onAdd}
          addLabel="Додати оператора"
        />

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                {headers.map((header, idx) => (
                  <TableCell className="text-black border-bottom" key={idx}>
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={11}>Завантаження...</TableCell>
                </TableRow>
              )}
              {error && (
                <TableRow>
                  <TableCell colSpan={11}>Помилка завантаження даних</TableCell>
                </TableRow>
              )}
              {!loading &&
                !error &&
                filteredOperators.map((op, idx) => (
                  <TableRow
                    key={op.operator_id ?? idx}
                    sx={{
                      cursor: onNavigate ? "pointer" : "default",
                    }}
                    className="border-bottom"
                    onClick={() => handleRowClick(op)}
                  >
                    <StyledTableCell>
                      <Typography variant="subtitle1">
                        {op.full_name ?? "-"}
                      </Typography>
                    </StyledTableCell>

                    <StyledTableCell>
                      <Typography variant="subtitle1">
                        {op.accepted_count ?? 0}
                      </Typography>
                    </StyledTableCell>

                    <StyledTableCell>
                      <Typography variant="subtitle1">
                        {op.orders_with_upsell ?? 0}
                      </Typography>
                    </StyledTableCell>

                    <StyledTableCell>
                      <Typography variant="subtitle1">
                        {op.orders_without_upsell ?? 0}
                      </Typography>
                    </StyledTableCell>

                    <StyledTableCell>
                      <Typography variant="subtitle1">
                        {op.cross_count ?? 0}
                      </Typography>
                    </StyledTableCell>

                    <StyledTableCell>
                      <Typography variant="subtitle1">
                        {op.upsell_margin_sum ?? 0}
                      </Typography>
                    </StyledTableCell>

                    <StyledTableCell>
                      <CustomChip
                        value={`${op.upsell_percent ?? 0}%`}
                        color={getKpiColor(
                          "upsell_percent",
                          op.upsell_percent ?? 0
                        )}
                      />
                    </StyledTableCell>

                    <StyledTableCell>
                      <CustomChip
                        value={op.avg_check ?? 0}
                        color={getKpiColor("avg_check", op.avg_check ?? 0)}
                      />
                    </StyledTableCell>

                    <StyledTableCell>
                      <CustomChip
                        value={op.leads_per_hour ?? 0}
                        color={getKpiColor(
                          "leads_per_hour",
                          op.leads_per_hour ?? 0
                        )}
                      />
                    </StyledTableCell>

                    <StyledTableCell>
                      <Typography variant="subtitle1">
                        {op.operator_salary ?? 0}
                      </Typography>
                    </StyledTableCell>

                    <TableCell sx={{ borderBottom: "inherit" }} align="right">
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                          alignItems: "center",
                        }}
                      >
                        <Button
                          variant="text"
                          sx={{
                            textTransform: "none",
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            padding: 0,
                          }}
                          onClick={(event) => {
                            event.stopPropagation();
                            console.log("More options clicked");
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <OperatorsTableInfoModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        modalType={modalType}
        modalRef={modalRef}
        onDateSelect={handleDateSelect}
        initialDate={dateForRequest}
      />
    </>
  );
}
